import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Truck, ArrowsClockwise, CaretRight, Warning, Lightbulb, Thermometer } from "@phosphor-icons/react";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActiveShipmentsPanel({ onSelectShipment, onViewAll }) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shipments/active?limit=6`);
      setShipments(res.data || []);
    } catch (e) {
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Refresh on shipment events / device events that may shift compliance
  const refetchTimer = useRef(null);
  const handleLive = useCallback((msg) => {
    if (!["shipment.created", "shipment.started", "shipment.delivered", "event.ingested", "alert.created"].includes(msg.type)) return;
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      fetch();
    }, 1500);
  }, [fetch]);
  useLiveUpdates(handleLive);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="active-shipments-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
          <Truck size={12} weight="duotone" className="text-teal-400" />
          Active Shipments
          <Badge variant="outline" className="border-zinc-700 text-[10px] font-mono ml-1">{shipments.length}</Badge>
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={onViewAll} className="text-[10px] font-mono text-teal-400 hover:text-teal-300"
            data-testid="view-all-shipments-btn">View all</button>
          <button onClick={fetch} className="text-zinc-500 hover:text-teal-400">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-4">Loading...</div>
      ) : shipments.length === 0 ? (
        <div className="text-center py-6">
          <Truck size={28} weight="duotone" className="text-zinc-700 mx-auto mb-2" />
          <p className="text-xs text-zinc-500 mb-1">No active shipments</p>
          <button onClick={onViewAll} className="text-[10px] font-mono text-teal-400 hover:text-teal-300"
            data-testid="empty-create-shipment">Create one →</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="active-shipments-list">
          {shipments.map(s => {
            const hasIssue = s.recent_excursions > 0 || s.light_breach;
            return (
              <button key={s.id} onClick={() => onSelectShipment(s.id)}
                data-testid={`active-ship-${s.id}`}
                className={`text-left p-3 rounded-sm border transition-all group ${
                  hasIssue
                    ? "bg-amber-500/5 border-amber-700/40 hover:border-amber-600 hover:bg-amber-500/10"
                    : "bg-zinc-950/50 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/40"
                }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Truck size={12} className={hasIssue ? "text-amber-400" : "text-teal-400"} />
                  <span className="text-xs font-mono font-semibold text-zinc-100 truncate flex-1">{s.name}</span>
                  <CaretRight size={11} className="text-zinc-600 group-hover:text-teal-400" />
                </div>
                {s.customer && <p className="text-[10px] text-zinc-500 truncate ml-4 mb-1">{s.customer}</p>}
                {(s.references || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 ml-4 mb-1.5">
                    {s.references.slice(0, 2).map((r, i) => (
                      <span key={i} className="text-[9px] font-mono text-zinc-500">
                        <span className="text-zinc-600">{r.type}:</span> {r.value}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 ml-4 text-[10px] font-mono">
                  <span className="text-zinc-400">{s.device_count} dev</span>
                  {s.latest_temp != null && (
                    <span className={hasIssue && s.recent_excursions > 0 ? "text-amber-400" : "text-zinc-400"}>
                      <Thermometer size={9} className="inline" /> {s.latest_temp}°F
                    </span>
                  )}
                  {s.recent_excursions > 0 && (
                    <span className="text-amber-400"><Warning size={9} className="inline" /> {s.recent_excursions}</span>
                  )}
                  {s.light_breach && (
                    <span className="text-amber-400"><Lightbulb size={9} className="inline" /> seal</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
